import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PROMPT_EDIT_ASPECT_RATIOS_FOR_GPT,
  PROMPT_EDIT_QUALITIES_FOR_FLUX_KONTEXT,
  PromptEditForm,
  PromptEditModel,
} from "domains/assets/constants/promptEdit";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import { getImageDimensions } from "domains/commons/utils/getImageDimensions";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  GetJobIdApiResponse,
  PostPromptEditingInferencesApiArg,
  useLazyGetAssetsByAssetIdQuery,
  useLazyGetJobIdQuery,
  usePostPromptEditingInferencesMutation,
} from "infra/api/generated/api";
import _ from "lodash";

export const PROMPT_EDIT_DEFAULT_FORM: PromptEditForm = {
  count: 1,
  reference: undefined,
  prompt: "",
  modelId: "gpt-image-1",
  refImages: [],
  aspectRatio: "auto",
  quality: "high",
  guidanceScale: 3.5,
  seed: undefined,
};

// ------------------------------------

export interface UseAssetPromptEditReturnValue {
  form: PromptEditForm;
  setValue: <K extends keyof PromptEditForm>(
    key: K,
    value: PromptEditForm[K]
  ) => void;
  setForm: React.Dispatch<React.SetStateAction<PromptEditForm>>;
  setReference: (
    asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
  ) => void;
  handlePromptEditJob: (props: {
    assetId: string;
    trackingExtraParams: Record<string, unknown>;
  }) => Promise<GetJobIdApiResponse["job"] | undefined>;
  handlePromptEditAsset: (props: {
    assetId: string;
    trackingExtraParams: Record<string, unknown>;
  }) => Promise<GetAssetsByAssetIdApiResponse["asset"] | undefined>;
  isPromptEditLoading?: boolean;
}

export default function useAssetPromptEdit(): UseAssetPromptEditReturnValue {
  const [isPromptEditLoading, setIsPromptEditLoading] =
    useState<boolean>(false);
  const [defaultModelId, setDefaultModelId] =
    usePersistedState<PromptEditModel>(
      PersistedStateKey.LAST_PROMPT_EDIT_MODEL_ID,
      {
        defaultValue: PROMPT_EDIT_DEFAULT_FORM.modelId,
      }
    );
  const [form, setForm] = useState<PromptEditForm>({
    ...PROMPT_EDIT_DEFAULT_FORM,
    modelId: defaultModelId,
  });
  const { selectedProject } = useTeamContext();
  const [getJobTrigger] = useLazyGetJobIdQuery();
  const [getAssetByAssetId] = useLazyGetAssetsByAssetIdQuery();
  const [triggerImagePromptEdit] = usePostPromptEditingInferencesMutation();
  const { infoToast, successToast, errorToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { showLimitModal } = usePlanContext();

  // ----------------------------------

  const handlePromptEdit = useCallback(
    async ({
      assetId,
      trackingExtraParams,
    }: {
      assetId: string;
      trackingExtraParams: Record<string, unknown>;
    }) => {
      infoToast({
        title: "Editing image",
      });

      try {
        const body = getPromptEditParamsBody({
          assetId,
          form,
          prompt: form.prompt,
        });

        const promptEditResponse = await triggerImagePromptEdit({
          projectId: selectedProject.id,
          body,
        })
          .unwrap()
          .then(removeCuFromType);

        Track(AnalyticsEvents.ImageLibrary.PromptEditedImage, {
          ...body,
          ...trackingExtraParams,
        });

        return promptEditResponse.job;
      } catch (error: any) {
        handleApiError(error, "Error enhancing image", {
          quota: () => {
            if (_.get(error, "data.details.remainingSeconds")) {
              showLimitModal("planCooldown", {
                timeout: error.data.details.remainingSeconds,
                type: "promptEditing",
              });
            } else if (
              _.get(error, "data.reason").includes(
                "You have reached your plan's limit."
              )
            ) {
              showLimitModal("notEnoughCreativeUnits");
            } else {
              errorToast({
                title: "Error generating images",
                description: _.get(error, "data.reason"),
              });
            }
          },
        });
      }
    },
    [
      form,
      selectedProject.id,
      infoToast,
      errorToast,
      handleApiError,
      showLimitModal,
      triggerImagePromptEdit,
    ]
  );

  const handlePromptEditJob = useCallback(
    async ({
      assetId,
      trackingExtraParams,
    }: {
      assetId: string;
      trackingExtraParams: Record<string, unknown>;
    }) => {
      setIsPromptEditLoading(true);
      const job = await handlePromptEdit({
        assetId,
        trackingExtraParams,
      });
      setIsPromptEditLoading(false);
      return job;
    },
    [handlePromptEdit]
  );

  const handlePromptEditAsset = useCallback(
    async ({
      assetId,
      trackingExtraParams,
    }: {
      assetId: string;
      trackingExtraParams: Record<string, unknown>;
    }) => {
      setIsPromptEditLoading(true);
      let job = await handlePromptEdit({
        assetId,
        trackingExtraParams,
      });

      while (
        job &&
        ["in-progress", "queued", "warming-up"].includes(job.status)
      ) {
        await new Promise((resolve) => setTimeout(resolve, 2_000));
        try {
          const response = await getJobTrigger({
            projectId: selectedProject.id,
            jobId: job.jobId,
          }).unwrap();
          job = response.job;
        } catch (_) {
          job = undefined;
        }
      }

      if (
        !job ||
        job.status === "failure" ||
        job.status === "canceled" ||
        !job.metadata.assetIds?.[0]
      ) {
        errorToast({
          title: "Error editing image",
        });
        return;
      }

      const asset = await getAssetByAssetId({
        projectId: selectedProject.id,
        originalAssets: "true",
        assetId: job.metadata.assetIds[0],
      }).unwrap();

      setIsPromptEditLoading(false);

      if (!asset || asset.asset.status === "error") {
        errorToast({
          title: "Error editing image",
        });
        return;
      }

      successToast({
        title: "The image has been edited",
      });

      return asset.asset;
    },
    [
      errorToast,
      getAssetByAssetId,
      getJobTrigger,
      handlePromptEdit,
      selectedProject.id,
      successToast,
    ]
  );

  // ----------------------------------

  const setValue = useCallback(
    <K extends keyof PromptEditForm>(key: K, value: PromptEditForm[K]) => {
      if (key === "seed" && !!value) {
        setForm((form) => ({
          ...form,
          [key]: value,
          count: 1,
        }));
        return;
      }

      if (key === "count" && typeof value === "number" && value > 1) {
        setForm((form) => ({
          ...form,
          [key]: value,
          seed: undefined,
        }));
        return;
      }

      if (key === "modelId" && value === "gpt-image-1") {
        setForm((form) => {
          const newAspectRatio = (
            PROMPT_EDIT_ASPECT_RATIOS_FOR_GPT as readonly string[]
          ).includes(form.aspectRatio)
            ? form.aspectRatio
            : "auto";
          return {
            ...form,
            [key]: value,
            aspectRatio: newAspectRatio,
          };
        });
        return;
      }

      if (key === "modelId" && value === "flux-kontext") {
        setForm((form) => {
          const newQuality = (
            PROMPT_EDIT_QUALITIES_FOR_FLUX_KONTEXT as readonly string[]
          ).includes(form.quality)
            ? form.quality
            : "high";
          return { ...form, [key]: value, quality: newQuality };
        });
        return;
      }

      setForm((form) => ({ ...form, [key]: value }));
    },
    [setForm]
  );

  const setReference = useCallback(
    async (asset: GetAssetsByAssetIdApiResponse["asset"] | undefined) => {
      const { width, height } = await (async () => {
        if (asset?.metadata.width && asset?.metadata.height) {
          return {
            width: asset.metadata.width,
            height: asset.metadata.height,
          };
        } else if (asset?.url) {
          return await getImageDimensions(asset.url);
        } else {
          return { width: undefined, height: undefined };
        }
      })();

      setForm((form) => ({
        ...form,
        reference: asset
          ? {
              ...asset,
              metadata: {
                ...asset.metadata,
                width,
                height,
              },
            }
          : undefined,
      }));
    },
    []
  );

  // ----------------------------------

  useEffect(() => {
    setDefaultModelId(form.modelId);
  }, [setDefaultModelId, form.modelId]);

  // ----------------------------------

  return useMemo(
    () => ({
      form,
      setForm,
      setValue,
      setReference,
      handlePromptEditJob,
      handlePromptEditAsset,
      isPromptEditLoading,
    }),
    [
      form,
      setForm,
      setValue,
      setReference,
      handlePromptEditJob,
      handlePromptEditAsset,
      isPromptEditLoading,
    ]
  );
}

// ------------------------------------

export function getPromptEditParamsBody({
  assetId,
  form,
  prompt,
}: {
  assetId: string;
  form: PromptEditForm;
  prompt: string;
}): PostPromptEditingInferencesApiArg["body"] {
  return {
    image: assetId,
    prompt,
    modelId: form.modelId,
    numSamples: form.count,
    referenceImages: form.refImages.map((styleImage) => styleImage.id),
    ...(form.modelId === "gpt-image-1" || form.modelId === "flux-kontext"
      ? {
          aspectRatio: form.aspectRatio,
          quality: form.quality,
        }
      : {}),
    ...(form.modelId === "flux-kontext"
      ? {
          guidanceScale: form.guidanceScale,
          seed: form.count === 1 && form.seed ? parseInt(form.seed) : undefined,
        }
      : {}),
  };
}
